import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaAssetDetailsResponse, MediaAssetResponse } from '@/core/generated/api';
import { mediaLibraryService } from '@/entities/MediaLibrary/api/mediaLibraryService';
import {
    formatMediaDimensions,
    formatMediaSize,
    formatMediaType,
    getMediaFolderPath,
} from '@/entities/MediaLibrary/model/format';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import type { MediaFolderResponse } from '@/core/generated/api';
import { getErrorMessage } from '@/shared/lib/errors';
import { SidePanel } from '@/shared/ui/SidePanel/SidePanel';
import { ImagePreviewDialog } from '@/shared/ui/ImagePreviewDialog/ImagePreviewDialog';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { useImageDimensions } from '@/shared/hooks/useImageDimensions';
import classes from './MediaLibrary.module.scss';

interface MediaDetailsPanelProps {
    id: string;
    folders: MediaFolderResponse[];
    onClose: () => void;
    onRename: (asset: MediaAssetResponse) => void;
    onMove: (asset: MediaAssetResponse) => void;
    onDownload: (asset: MediaAssetResponse) => void;
    onDelete: (asset: MediaAssetResponse) => void;
    onMetadataUpdated?: () => Promise<void>;
}

/** Показывает изображение, свойства, использование и историю */
export const MediaDetailsPanel = (
    { id, folders, onClose, onRename, onMove, onDownload, onDelete, onMetadataUpdated }: MediaDetailsPanelProps
) => {
    const [details, setDetails] = useState<MediaAssetDetailsResponse>();
    const [error, setError] = useState('');
    const [tab, setTab] = useState('details');
    const [metadataLoading, setMetadataLoading] = useState(false);
    const [metadataError, setMetadataError] = useState('');
    const [previewId, setPreviewId] = useState<string>();
    const canImport = useCanAccessPermission('admin.media-library.import');
    const { recordDimensions, getDimensions } = useImageDimensions();

    useEffect(() => {
        let active = true;
        setDetails(undefined);
        setPreviewId(undefined);
        setError('');
        setMetadataError('');
        setMetadataLoading(false);

        /** Получает карточку и уточняет отсутствующие метаданные с разрешённым импортом */
        const load = async (): Promise<void> => {
            try {
                const result = await mediaLibraryService.details(id);

                if (!active) return;

                setDetails(result);
                const file = result.asset;
                const raster = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file?.contentType ?? '');

                const completeMetadata = (file?.size ?? 0) > 0
                    && (file?.width ?? 0) > 0 && (file?.height ?? 0) > 0;

                if (!canImport || completeMetadata || !file?.objectKey || !raster) return;

                setMetadataLoading(true);

                try {
                    await mediaLibraryService.import({ keys: [file.objectKey] });
                    const enriched = await mediaLibraryService.details(id);

                    if (active) {
                        setDetails(enriched);
                        await onMetadataUpdated?.();
                    }
                } catch (failure: unknown) {
                    if (active) setMetadataError(getErrorMessage(failure, 'Не удалось уточнить размеры изображения в S3'));
                } finally {
                    if (active) setMetadataLoading(false);
                }
            } catch (failure: unknown) {
                if (active) setError(getErrorMessage(failure, 'Не удалось получить сведения о файле'));
            }
        };

        void load();

        return () => { active = false; };
    }, [id, canImport, onMetadataUpdated]);

    /** Копирует постоянную ссылку с обработкой запрета буфера обмена */
    const copy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(details?.asset?.url ?? '');
        } catch {
            setError('Браузер запретил копирование. Ссылку можно выделить в свойствах файла.');
        }
    };

    const asset = details?.asset;
    const dimensions = getDimensions(asset?.url, asset?.width, asset?.height);
    const usage = details?.usage ?? [];

    return <>
        <SidePanel
            className={classes.detailsPanel}
            visible
            title="Сведения о файле"
            onHide={onClose}
        >
            {error &&
                <RequestError message={error} />
            }
            {!details && !error &&
                <RequestLoading />
            }
            {asset &&
                <div className={classes.details}>
                    <button
                        type="button"
                        className={classes.detailPreview}
                        aria-label="Открыть изображение на весь экран"
                        disabled={!asset.url}
                        onClick={() => setPreviewId(id)}
                    >
                        <img
                            src={asset.url ?? ''}
                            alt={asset.name ?? ''}
                            onLoad={event => recordDimensions(asset.url, event.currentTarget)}
                        />
                        <span>
                            <i
                                className="pi pi-expand"
                                aria-hidden="true"
                            /> На весь экран</span>
                    </button>
                    <h2>
                        {asset.name}
                    </h2>
                    <p>
                        {formatMediaType(asset.contentType)} · {formatMediaSize(asset.size)} · {formatMediaDimensions(dimensions?.width, dimensions?.height)}
                    </p>
                    {metadataLoading &&
                        <p
                            className={classes.note}
                            role="status"
                        >
                            Получаем размеры изображения из S3…
                        </p>
                    }
                    {metadataError &&
                        <RequestError message={metadataError} />
                    }
                    <div
                        className={classes.tabs}
                        role="tablist"
                        aria-label="Сведения о файле"
                    >
                        {[{
                            id: 'details',
                            label: 'Свойства'
                        }, {
                            id: 'usage',
                            label: `Использование (${usage.length})`
                        }, {
                            id: 'history',
                            label: 'История'
                        }].map(item => <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === item.id}
                            className={tab === item.id
                                ? classes.activeTab
                                : ''
                            }
                            onClick={() => setTab(item.id)}
                        >
                            {item.label}
                        </button>)}
                    </div>
                    <div role="tabpanel">
                        {tab === 'details' &&
                            <>
                                <dl className={classes.properties}>
                                    <dt>Название</dt>
                                    <dd>
                                        {asset.name}
                                    </dd>
                                    <dt>Исходное имя</dt>
                                    <dd>
                                        {asset.originalName}
                                    </dd>
                                    <dt>Папка</dt>
                                    <dd>
                                        {getMediaFolderPath(folders, asset.folderId)}
                                    </dd>
                                    <dt>Добавлен в каталог</dt>
                                    <dd>
                                        {asset.created
                                            ? new Date(asset.created).toLocaleString('ru-RU')
                                            : '—'}
                                    </dd>
                                    <dt>Добавил сотрудник</dt>
                                    <dd>
                                        {asset.uploadedBy ?? 'Нет сведений'}
                                    </dd>
                                    <dt>Ключ S3</dt>
                                    <dd>
                                        {asset.objectKey}
                                    </dd>
                                    <dt>Постоянная ссылка</dt>
                                    <dd>
                                        <a
                                            href={asset.url ?? ''}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {asset.url}
                                        </a>
                                    </dd>
                                </dl>
                                <div className={classes.tags}>
                                    {asset.tags?.map(tag => <span key={tag}>
                                        {tag}
                                    </span>)}
                                </div>
                            </>
                        }
                        {tab === 'usage' &&
                            <>
                                {usage.length
                                    ? <>
                                        <div className={classes.notice}>
                                            <i
                                                aria-hidden="true"
                                                className="pi pi-link"
                                            /> Файл используется. Ниже указаны объекты и разделы. Удаление заблокировано.</div>
                                        {usage.map(item => item.route
                                            ? <Link
                                                key={`${item.kind}-${item.id}`}
                                                className={classes.usageLink}
                                                to={item.route ?? ''}
                                            >
                                                <i
                                                    aria-hidden="true"
                                                    className="pi pi-external-link"
                                                />
                                                <div>
                                                    <strong>
                                                        {item.name}
                                                    </strong>
                                                    <small>
                                                        {item.kind}
                                                    </small>
                                                </div>
                                            </Link>
                                            : <div
                                                key={`${item.kind}-${item.id}`}
                                                className={classes.usageLink}
                                            >
                                                <strong>
                                                    {item.name}
                                                </strong>
                                            </div>)}
                                    </>
                                    : <p className={classes.note}>В основной базе ссылок не найдено. Вложения чатов проверяются отдельно.</p>}
                            </>
                        }
                        {tab === 'history' &&
                            <ol className={classes.history}>
                                {details.history?.map((item, index) => <li key={`${item.created}-${index}`}>
                                    <strong>
                                        {item.description}
                                    </strong>
                                    <small>
                                        {item.created
                                            ? new Date(item.created).toLocaleString('ru-RU')
                                            : ''}
                                    </small>
                                    <small>
                                        {item.actorId
                                            ? `Сотрудник: ${item.actorId}`
                                            : 'Сотрудник не указан'}
                                    </small>
                                </li>)}
                            </ol>
                        }
                    </div>
                    <div className={classes.detailActions}>
                        <ActionButton
                            type="button"
                            icon="pi pi-copy"
                            outlined
                            color="secondary"
                            onClick={() => void copy()}
                        >
                            Копировать ссылку
                        </ActionButton>
                        <ActionButton
                            type="button"
                            icon="pi pi-download"
                            permission="admin.media-library.download"
                            onClick={() => onDownload(asset)}
                        >
                            Скачать
                        </ActionButton>
                        <ActionButton
                            type="button"
                            outlined
                            color="secondary"
                            permission="admin.media-library.manage"
                            disabled={metadataLoading}
                            onClick={() => onRename(asset)}
                        >
                            Переименовать
                        </ActionButton>
                        <ActionButton
                            type="button"
                            outlined
                            color="secondary"
                            permission="admin.media-library.manage"
                            disabled={metadataLoading}
                            onClick={() => onMove(asset)}
                        >
                            Переместить
                        </ActionButton>
                        <ActionButton
                            type="button"
                            outlined
                            color="danger"
                            permission="admin.media-library.delete"
                            disabled={metadataLoading || usage.length > 0}
                            onClick={() => onDelete(asset)}
                        >
                            Удалить из каталога
                        </ActionButton>
                    </div>
                </div>
            }
        </SidePanel>
        <ImagePreviewDialog
            imageUrl={previewId === id
                ? asset?.url ?? null
                : null
            }
            alt={asset?.name ?? 'Изображение в полном размере'}
            onClose={() => setPreviewId(undefined)}
        />
    </>;
};
